import type { StudioId } from '@/domain/types';
import { useBrainStore } from '@/state/brainStore';
import { buildQueue, partitionQueue } from '@/domain/queueEngine';
import { reportingNow } from '@/domain/reportingEngine';

/**
 * Store-derived operational counts for the Network View (Prompt K, K3). Replaces
 * the old hardcoded STUDIO_DECORATIONS map - every number here is read live from
 * the store. Queue depth and missed calls come from the one queue engine (the
 * same `buildQueue` the queue screens and `queueHealthByStudio` use), so the
 * counts can never drift from the real queue.
 */

export interface StudioNetworkCounts {
  studioId: StudioId;
  name: string;
  /** New + qualifying leads whose home studio is this one. */
  inquiries: number;
  /** Paid deposits with no booking yet - money in, still needs a date. */
  depositsReady: number;
  /** Unreturned inbound calls surfaced in this studio's queue. */
  missedCalls: number;
  /** Depth of this studio's working queue (the partitioned main lane). */
  queueDepth: number;
}

export function networkStudioCounts(now: string = reportingNow()): StudioNetworkCounts[] {
  const {
    customers,
    bookings,
    deposits,
    pieces,
    artists,
    rebookFollowupTasks,
    sequenceState,
    studios,
  } = useBrainStore.getState();

  return Object.values(studios)
    .filter((s) => !s.archived)
    .map((s) => {
      const items = buildQueue({
        customers,
        bookings,
        deposits,
        pieces,
        artists,
        rebookFollowupTasks,
        studioIds: [s.id],
        nowIso: now,
        sequenceState,
      });
      const { main } = partitionQueue(items);
      const missedCalls = items.filter((i) => i.kind === 'missed-call').length;
      const inquiries = Object.values(customers).filter(
        (c) => c.primaryStudioId === s.id && (c.leadStage === 'new' || c.leadStage === 'qualifying'),
      ).length;
      const depositsReady = Object.values(deposits).filter(
        (d) => d.studioId === s.id && d.status === 'paid' && !d.bookingId,
      ).length;
      return { studioId: s.id, name: s.name, inquiries, depositsReady, missedCalls, queueDepth: main.length };
    });
}

export interface NetworkTotals {
  inquiries: number;
  depositsReady: number;
  missedCalls: number;
  queueDepth: number;
}

/** Sum each operational count across all studios, for the network KPI tiles. */
export function networkTotals(counts: readonly StudioNetworkCounts[]): NetworkTotals {
  return counts.reduce<NetworkTotals>(
    (acc, c) => ({
      inquiries: acc.inquiries + c.inquiries,
      depositsReady: acc.depositsReady + c.depositsReady,
      missedCalls: acc.missedCalls + c.missedCalls,
      queueDepth: acc.queueDepth + c.queueDepth,
    }),
    { inquiries: 0, depositsReady: 0, missedCalls: 0, queueDepth: 0 },
  );
}
